/**
 * Utility functions for calculating date ranges
 */

export type DateFilterOption =
  | "all"
  | "this-week"
  | "this-month"
  | "next-month";

interface DateRange {
  from: Date;
  to: Date;
}

/**
 * Calculate date range based on a predefined filter option
 */
export const getDateRangeFromFilter = (
  filter: DateFilterOption,
): DateRange | null => {
  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );

  switch (filter) {
    case "all":
      return null;

    case "this-week": {
      const startOfWeek = new Date(startOfToday);
      startOfWeek.setDate(startOfToday.getDate() - startOfToday.getDay());
      const endOfWeek = new Date(
        startOfWeek.getTime() + 7 * 24 * 60 * 60 * 1000 - 1,
      );
      return { from: startOfWeek, to: endOfWeek };
    }

    case "this-month": {
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(
        now.getFullYear(),
        now.getMonth() + 1,
        0,
        23,
        59,
        59,
        999,
      );
      return { from: startOfMonth, to: endOfMonth };
    }

    case "next-month": {
      const startOfNextMonth = new Date(
        now.getFullYear(),
        now.getMonth() + 1,
        1,
      );
      const endOfNextMonth = new Date(
        now.getFullYear(),
        now.getMonth() + 2,
        0,
        23,
        59,
        59,
        999,
      );
      return { from: startOfNextMonth, to: endOfNextMonth };
    }

    default:
      return null;
  }
};

/**
 * Convert a Date to ISO 8601 string for API
 */
export const toISOString = (date: Date): string => {
  return date.toISOString();
};
